import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMantineColorScheme } from "@mantine/core";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import ExportingModule from "highcharts/modules/exporting";
import { useTranslation } from "react-i18next";
import { formatEnUsValue, formatProjectNumber, useCurrency } from "../../utils/helpers";
import "./cashFlowChartStyle.scss";

if (typeof ExportingModule === "function") {
  ExportingModule(Highcharts);
} else if (typeof ExportingModule?.default === "function") {
  ExportingModule.default(Highcharts);
}

const SERIES_META = [
  { key: "totalCapex", color: "#4992ff" },
  { key: "totalOpex", color: "#7cffb2" },
  { key: "totalIncentive", color: "#0031f6"},
  { key: "totalFuel", color: "#ff6e76" },
  { key: "totalReplacement", color: "#033b8e"},
  { key: "totalSalvage", color: "#05c091" },
];
const YEAR_BY_YEAR_ASSET_SERIES_META = [
  { key: "totalOpexFuel", labelKey: "result.cashFlow.series.yearByYear.opexFuel", color: "#4992ff" },
  { key: "totalCapex", labelKey: "result.cashFlow.series.yearByYear.capex", color: "#ff7043" },
  { key: "totalReplacement", labelKey: "result.cashFlow.series.yearByYear.replacement", color: "#033b8e" },
  { key: "totalSalvage", labelKey: "result.cashFlow.series.yearByYear.salvage", color: "#51c7fe" },
  { key: "totalIncentive", labelKey: "result.cashFlow.series.yearByYear.incentive", color: "#0031f6" },
];
const CUMULATIVE_ASSET_SERIES_META = [
  { key: "netCashFlow", labelKey: "result.cashFlow.series.cumulative.netCashFlow", color: "#4992ff" },
];

const Y_AXIS_TICK_COUNT = 6;
const Y_AXIS_MAX_DECIMALS = 6;

const normalizeTickKey = (value) => Number(value).toFixed(10);

const niceTickInterval = (min, max, desiredTicks = Y_AXIS_TICK_COUNT) => {
  const span = Math.abs(max - min);
  if (!Number.isFinite(span) || span === 0) return 1;

  const rawInterval = span / Math.max(desiredTicks - 1, 1);
  const exponent = Math.floor(Math.log10(rawInterval));
  const fraction = rawInterval / 10 ** exponent;

  let niceFraction = 1;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 2.5) niceFraction = 2.5;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;

  return niceFraction * 10 ** exponent;
};

const formatCompactAxisValue = (value, decimals = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "0";

  const sign = n < 0 ? "-" : "";
  const absValue = Math.abs(n);
  const units = [
    { divisor: 1e12, suffix: "T" },
    { divisor: 1e9, suffix: "B" },
    { divisor: 1e6, suffix: "M" },
    { divisor: 1e3, suffix: "K" },
  ];

  const unit = units.find(({ divisor }) => absValue >= divisor);
  if (!unit) {
    return formatProjectNumber(n, {
      maximumFractionDigits: decimals,
    });
  }

  const scaled = absValue / unit.divisor;
  const formatted = formatProjectNumber(scaled, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });

  return `${sign}${formatted}${unit.suffix}`;
};

const buildYAxisTickMeta = (tickValues) => {
  const sortedTicks = Array.from(
    new Set((Array.isArray(tickValues) ? tickValues : []).filter((value) => Number.isFinite(value)))
  ).sort((a, b) => a - b);

  if (sortedTicks.length === 0) {
    return { precision: 0, labelsByTick: new Map() };
  }

  for (let precision = 0; precision <= Y_AXIS_MAX_DECIMALS; precision += 1) {
    const labels = sortedTicks.map((value) => formatCompactAxisValue(value, precision));
    if (new Set(labels).size === labels.length) {
      return {
        precision,
        labelsByTick: new Map(sortedTicks.map((value, index) => [normalizeTickKey(value), labels[index]])),
      };
    }
  }

  return {
    precision: Y_AXIS_MAX_DECIMALS,
    labelsByTick: new Map(
      sortedTicks.map((value) => [normalizeTickKey(value), formatCompactAxisValue(value, Y_AXIS_MAX_DECIMALS)])
    ),
  };
};

const formatYAxisValue = (value, axis) => {
  const tickKey = normalizeTickKey(value);
  const labelMap = axis?.__cashFlowYAxisLabelsByTick;
  if (labelMap instanceof Map && labelMap.has(tickKey)) {
    return labelMap.get(tickKey);
  }

  return formatCompactAxisValue(value, axis?.__cashFlowYAxisPrecision ?? 0);
};

const createXAxisLabelFormatter = (visibleYears) => function formatXAxisLabel() {
  if (visibleYears.length > 25) {
    return `${this.value}`.replace(/^[^0-9]+/, "");
  }
  return this.value;
};

const createYAxisTickPositioner = () => function positionYAxisTicks() {
  const min = Number.isFinite(this.min) ? this.min : 0;
  const max = Number.isFinite(this.max) ? this.max : 0;

  if (min === max) {
    const singletonTicks = [min];
    const singletonMeta = buildYAxisTickMeta(singletonTicks);
    this.__cashFlowYAxisPrecision = singletonMeta.precision;
    this.__cashFlowYAxisLabelsByTick = singletonMeta.labelsByTick;
    return singletonTicks;
  }

  const interval = niceTickInterval(min, max, Y_AXIS_TICK_COUNT);
  const safeInterval = Number.isFinite(interval) && interval > 0 ? interval : 1;
  const start = Math.floor(min / safeInterval) * safeInterval;
  const end = Math.ceil(max / safeInterval) * safeInterval;
  const ticks = [];

  for (let tick = start; tick <= end + safeInterval / 1e6; tick += safeInterval) {
    ticks.push(Number(tick.toFixed(10)));
  }

  const meta = buildYAxisTickMeta(ticks);
  this.__cashFlowYAxisPrecision = meta.precision;
  this.__cashFlowYAxisLabelsByTick = meta.labelsByTick;
  return ticks;
};

const formatYAxisLabel = function formatYAxisLabel() {
  return formatYAxisValue(this.value, this.axis);
};

const positionCashFlowTooltip = function positionCashFlowTooltip(labelWidth, labelHeight, point) {
  const chart = this.chart;
  const hoverPoint = chart?.hoverPoints?.[0]?.point || chart?.hoverPoint || null;
  const anchor = point && Number.isFinite(point.plotX) ? point : hoverPoint;
  const plotLeft = chart.plotLeft;
  const plotTop = chart.plotTop;
  const plotWidth = chart.plotWidth;
  const plotHeight = chart.plotHeight;

  const anchorX = Number.isFinite(anchor?.plotX) ? anchor.plotX : plotWidth / 2;
  const anchorY = Number.isFinite(anchor?.plotY) ? anchor.plotY : plotHeight / 2;

  let x = plotLeft + anchorX - labelWidth / 2;
  x = Math.max(plotLeft + 4, Math.min(x, plotLeft + plotWidth - labelWidth - 4));

  let y = plotTop + anchorY - labelHeight - 10;
  y = Math.max(plotTop + 4, y);

  return { x, y };
};

const createCashFlowTooltipFormatter = (visibleYears, t, currency, seriesMetadata) => function formatCashFlowTooltip() {
  const firstPointIndex = Number.isInteger(this.points?.[0]?.point?.index)
    ? this.points[0].point.index
    : null;
  const title = (() => {
    if (firstPointIndex != null && visibleYears[firstPointIndex]) {
      return visibleYears[firstPointIndex];
    }
    if (typeof this.x === "string" && this.x.trim()) {
      return this.x;
    }
    if (Number.isFinite(this.x)) {
      return `${t("result.cashFlow.year")} ${Math.round(this.x)}`;
    }
    return `${t("result.cashFlow.year")} 0`;
  })();

  const points = (this.points || [])
    .filter((point) => point?.series?.visible !== false)
    .filter((point) => Number.isFinite(point.y) && point.y !== 0);

  const rows = points.map((point) => {
    const color = seriesMetadata.find((entry) => entry.key === point.series.options.id)?.color || point.color || "#666";
    return `<div><span style="color:${color}">●</span> ${point.series.name}: <b>${currency} ${formatEnUsValue(point.y)}</b></div>`;
  });

  return rows.length > 0 ? `<strong>${title}</strong><br/>${rows.join("")}` : `<strong>${title}</strong>`;
};

const handleCashFlowPointMouseOver = function handleCashFlowPointMouseOver() {
  const graphic = this.graphic;
  if (!graphic) return;

  const x = Number(graphic.attr("x"));
  const y = Number(graphic.attr("y"));
  const width = Number(graphic.attr("width"));
  const height = Number(graphic.attr("height"));
  if (![x, y, width, height].every(Number.isFinite)) return;

  this.__baseRect = { x, y, width, height };
  const scale = 1.06;
  const dx = (width * scale - width) / 2;
  const dy = (height * scale - height) / 2;

  graphic.animate(
    {
      x: x - dx,
      y: y - dy,
      width: width * scale,
      height: height * scale,
    },
    { duration: 100 },
  );
};

const handleCashFlowPointMouseOut = function handleCashFlowPointMouseOut() {
  const graphic = this.graphic;
  const base = this.__baseRect;
  if (!graphic || !base) return;

  graphic.animate(
    {
      x: base.x,
      y: base.y,
      width: base.width,
      height: base.height,
    },
    { duration: 100 },
  );
};

const csvEscape = (val) => {
  const s = String(val);
  if (/[",\n\r]/.test(s) || /[^\x20-\x7E]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const CashFlowChart = ({ data = [], isDiscounted = false, isCumulative = false }) => {
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  const currency = useCurrency();
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [hiddenKeys, setHiddenKeys] = useState(() => new Set());
  const [zoomRange, setZoomRange] = useState([0, 1]);
  const isAssetYearByYear = Array.isArray(data?.assets) && !isCumulative;
  const fontFamily = "Nunito, sans-serif";
  const legendTextColor = colorScheme === "dark" ? "#d5d7e0" : "#495057";

  const normalized = useMemo(() => {
    if (Array.isArray(data?.assets)) {
      const valueKey = isCumulative
        ? isDiscounted
          ? "cumulativeDiscountedValues"
          : "cumulativeNominalValues"
        : isDiscounted
          ? "relativeDiscountedValues"
          : "relativeNominalValues";
      const costTypesBySeries = isAssetYearByYear
        ? {
            totalOpexFuel: ["OPEX", "Fuel"],
            totalCapex: ["CAPEX"],
            totalReplacement: ["Replacement"],
            totalSalvage: ["Salvage"],
            totalIncentive: ["Incentive"],
          }
        : isCumulative
          ? {
              netCashFlow: ["CAPEX", "OPEX", "Fuel", "Replacement", "Salvage", "Incentive"],
            }
        : {
            totalCapex: ["CAPEX"],
            totalOpex: ["OPEX"],
            totalIncentive: ["Incentive"],
            totalFuel: ["Fuel"],
            totalReplacement: ["Replacement"],
            totalSalvage: ["Salvage"],
          };
      const yearCount = Math.max(
        0,
        ...data.assets.flatMap((asset) =>
          (asset?.cashFlows || []).map((cashFlow) =>
            Array.isArray(cashFlow?.[valueKey]) ? cashFlow[valueKey].length : 0
          )
        )
      );

      const yearlyTotals = Array.from({ length: yearCount }, (_, yearIndex) =>
        Object.fromEntries(
          Object.entries(costTypesBySeries).map(([seriesKey, costTypes]) => [
            seriesKey,
            data.assets.reduce((total, asset) => {
              const value = (asset?.cashFlows || [])
                .filter((cashFlow) => costTypes.includes(cashFlow?.costType))
                .reduce((assetTotal, cashFlow) => {
                  const amount = Number(cashFlow?.[valueKey]?.[yearIndex] ?? 0);
                  return assetTotal + (Number.isFinite(amount) ? amount : 0);
                }, 0);
              return total + value;
            }, 0),
          ])
        )
      );
      if (!isCumulative) return yearlyTotals;

      return yearlyTotals.map((row) => ({ netCashFlow: row.netCashFlow }));
    }

    return (Array.isArray(data) ? data : []).map((item) => {
      const source = isDiscounted ? item?.discountedValue : item?.nominalValue;
      return {
        totalCapex: Number(source?.totalCapex ?? 0),
        totalOpex: Number(source?.totalOpex ?? 0),
        totalIncentive: Number(source?.totalIncentive ?? 0),
        totalFuel: Number(source?.totalFuel ?? 0),
        totalReplacement: Number(source?.totalReplacement ?? 0),
        totalSalvage: Number(source?.totalSalvage ?? 0),
      };
    });
  }, [data, isCumulative, isDiscounted]);

  const years = useMemo(
    () => normalized.map((_, index) => `${t("result.cashFlow.year")} ${index}`),
    [normalized, t]
  );

  const seriesWithLabels = useMemo(
    () =>
      (isAssetYearByYear
        ? YEAR_BY_YEAR_ASSET_SERIES_META
        : Array.isArray(data?.assets) && isCumulative
          ? CUMULATIVE_ASSET_SERIES_META
          : SERIES_META
      ).map((series) => ({
        ...series,
        label: t(series.labelKey || `result.cashFlow.series.${series.key}`),
      })),
    [data, isAssetYearByYear, isCumulative, t]
  );

  const totalYears = years.length;
  let [zoomStart, zoomEnd] = zoomRange;
  zoomStart = Math.max(0, Math.min(1, zoomStart));
  zoomEnd = Math.max(zoomStart, Math.min(1, zoomEnd));

  const startIdx = Math.floor(zoomStart * totalYears);
  let endIdx = Math.ceil(zoomEnd * totalYears);
  if (endIdx <= startIdx) endIdx = Math.min(totalYears, startIdx + 1);

  const visibleYears = years.slice(startIdx, endIdx);
  const visibleData = normalized.slice(startIdx, endIdx);
  const activeSeries = seriesWithLabels.filter((series) => !hiddenKeys.has(series.key));

  const chartSeries = useMemo(
    () =>
      activeSeries.map((series) => ({
        id: series.key,
        name: series.label,
        type: "column",
        stack: "cashflow",
        color: series.color,
        visible: true,
        borderWidth: 0,
        borderRadius: 0,
        pointPadding: 0.02,
        groupPadding: 0.12,
        data: visibleData.map((row) => (Number.isFinite(row?.[series.key]) ? row[series.key] : 0)),
      })),
    [activeSeries, visibleData]
  );

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "column",
        height: 500,
        backgroundColor: colorScheme === "dark" ? "#111111" : "#ffffff",
        style: { fontFamily },
        animation: false,
      },
      title: {
        text: t("result.cashFlow.title"),
        align: "left",
        style: { color: legendTextColor },
      },
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories: visibleYears,
        title: { text: t("result.cashFlow.axis.xYears"), style: { color: legendTextColor } },
        labels: {
          style: { color: legendTextColor, fontSize: "11px", fontWeight: 500 },
          formatter: createXAxisLabelFormatter(visibleYears),
        },
      },
      yAxis: {
        gridLineWidth: 0.5,
        gridLineColor: colorScheme === "dark" ? "#2e2e2e" : "#e6e6e6",
        title: { text: `${t("result.cashFlow.axis.yCost")} (${currency})`, style: { color: legendTextColor } },
        tickPositioner: createYAxisTickPositioner(),
        labels: {
          style: { color: legendTextColor, fontSize: "11px", fontWeight: 500 },
          formatter: formatYAxisLabel,
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        followPointer: false,
        backgroundColor: colorScheme === "dark" ? "#222" : "#fff",
        borderColor: colorScheme === "dark" ? "#555" : "#666",
        style: {
          color: colorScheme === "dark" ? "#e0e0e0" : "#222",
          pointerEvents: "none",
        },
        positioner: positionCashFlowTooltip,
        formatter: createCashFlowTooltipFormatter(visibleYears, t, currency, seriesWithLabels),
      },
      plotOptions: {
        series: {
          animation: false,
          states: {
            inactive: { opacity: 0.22 },
          },
        },
        column: {
          stacking: "normal",
          borderWidth: 0,
          borderRadius: 0,
          pointPadding: 0.02,
          groupPadding: 0.12,
          shadow: false,
          states: {
            hover: {
              brightness: 0.08,
            },
          },
          point: {
            events: {
              mouseOver: handleCashFlowPointMouseOver,
              mouseOut: handleCashFlowPointMouseOut,
            },
          },
        },
      },
      series: chartSeries,
    }),
    [chartSeries, colorScheme, currency, fontFamily, legendTextColor, seriesWithLabels, t, visibleYears]
  );

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === "undefined") return undefined;

    resizeObserverRef.current = new ResizeObserver(() => {
      chartRef.current?.chart?.reflow();
    });
    resizeObserverRef.current.observe(containerRef.current);

    return () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
    };
  }, []);

  useEffect(() => {
    chartRef.current?.chart?.reflow();
  }, [chartOptions]);

  const zoomIn = () => {
    if (totalYears <= 1) return;
    const range = zoomEnd - zoomStart;
    const minSpan = 1 / totalYears;
    if (range <= minSpan) return;

    const nextSpan = Math.max(range / 2, minSpan);
    const center = zoomStart + range / 2;
    let nextStart = center - nextSpan / 2;
    let nextEnd = center + nextSpan / 2;

    if (nextStart < 0) {
      nextEnd -= nextStart;
      nextStart = 0;
    }
    if (nextEnd > 1) {
      const overflow = nextEnd - 1;
      nextStart -= overflow;
      nextEnd = 1;
    }

    setZoomRange([Math.max(0, nextStart), Math.min(1, nextEnd)]);
  };

  const zoomOut = () => {
    if (totalYears <= 1) return;
    const range = zoomEnd - zoomStart;
    const nextSpan = Math.min(range * 2, 1);
    const center = zoomStart + range / 2;
    let nextStart = center - nextSpan / 2;
    let nextEnd = center + nextSpan / 2;

    if (nextStart < 0) {
      nextEnd -= nextStart;
      nextStart = 0;
    }
    if (nextEnd > 1) {
      const overflow = nextEnd - 1;
      nextStart -= overflow;
      nextEnd = 1;
    }

    setZoomRange([Math.max(0, nextStart), Math.min(1, nextEnd)]);
  };

  const zoomReset = () => {
    setZoomRange([0, 1]);
    setHiddenKeys(new Set());
  };

  const handleDownload = (value) => {
    const chart = chartRef.current?.chart;

    if (value === "png") {
      if (!chart) return;

      const svg = typeof chart.getSVG === "function" ? chart.getSVG() : chart.container?.querySelector("svg")?.outerHTML;
      if (!svg) return;

      const serializer = new XMLSerializer();
      const svgDoc = new DOMParser().parseFromString(svg, "image/svg+xml");
      const svgNode = svgDoc.documentElement.cloneNode(true);
      const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      bg.setAttribute("x", "0");
      bg.setAttribute("y", "0");
      bg.setAttribute("width", "100%");
      bg.setAttribute("height", "100%");
      bg.setAttribute("fill", colorScheme === "dark" ? "#111111" : "#ffffff");
      svgNode.insertBefore(bg, svgNode.firstChild);
      const svgString = serializer.serializeToString(svgNode);
      const img = new Image();
      const canvas = document.createElement("canvas");
      const width = chart.chartWidth || 1200;
      const height = chart.chartHeight || 500;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        canvas.toBlob((blob) => {
          if (!blob) return;
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = `cash-flow-${new Date().toISOString().replace(/[:.]/g, "-")}.png`;
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          URL.revokeObjectURL(url);
        });
      };

      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
      return;
    }

    if (value === "csv") {
      try {
        const rows = [];
        const headerCols = [t("result.cashFlow.year"), ...seriesWithLabels.map((series) => `${series.label} (${currency})`)];
        rows.push(headerCols.map(csvEscape).join(","));

        normalized.forEach((row, index) => {
          const yearLabel = `${t("result.cashFlow.year")} ${index}`;
          const values = seriesWithLabels.map((series) => (row[series.key] !== undefined ? row[series.key] : ""));
          rows.push([csvEscape(yearLabel), ...values].join(","));
        });

        const csvContent = rows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        anchor.download = `cash-flow-${ts}.csv`;
        anchor.href = url;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        // Silent fail
      }
    }
  };

  return (
    <div className='chart-container' ref={containerRef} style={{ width: '100%', height: 'auto', maxWidth: '100%', fontFamily, zoom: '118%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '4px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            aria-label="cashflow-zoom-in"
            title="Zoom In"
            onClick={zoomIn}
            style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', background: colorScheme === 'dark' ? '#222832' : '#ffffff', color: colorScheme === 'dark' ? '#d5d7e0' : '#495057' }}
          >
            +
          </button>
          <button
            type="button"
            aria-label="cashflow-zoom-out"
            title="Zoom Out"
            onClick={zoomOut}
            style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', background: colorScheme === 'dark' ? '#222832' : '#ffffff', color: colorScheme === 'dark' ? '#d5d7e0' : '#495057' }}
          >
            -
          </button>
          <button
            type="button"
            aria-label="cashflow-reset-zoom"
            title="Reset Zoom"
            onClick={zoomReset}
            style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', background: colorScheme === 'dark' ? '#222832' : '#ffffff', color: colorScheme === 'dark' ? '#d5d7e0' : '#495057' }}
          >
            Reset
          </button>
          <select
            title={t('common.download')}
            style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', background: colorScheme === 'dark' ? '#222832' : '#ffffff', color: colorScheme === 'dark' ? '#d5d7e0' : '#495057' }}
            value=""
            onChange={(e) => {
              handleDownload(e.target.value);
              e.target.selectedIndex = 0;
            }}
          >
            <option value="" disabled hidden>
              {t('common.download')}
            </option>
            <option value="png">PNG</option>
            <option value="csv">CSV</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, padding: '6px 0 0 0' }}>
        {seriesWithLabels.map((series) => {
          const isHidden = hiddenKeys.has(series.key);
          return (
            <button
              key={series.key}
              type="button"
              onClick={() => {
                setHiddenKeys((prev) => {
                  const next = new Set(prev);
                  if (next.has(series.key)) next.delete(series.key);
                  else next.add(series.key);
                  return next;
                });
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                margin: '0 10px 6px 0',
                padding: '4px 10px',
                fontSize: '12px',
                fontFamily,
                borderRadius: '4px',
                border: `1px solid ${colorScheme === 'dark' ? '#444' : '#ccc'}`,
                background: colorScheme === 'dark' ? (isHidden ? '#1c1c1c' : '#222832') : isHidden ? '#f5f5f5' : '#ffffff',
                opacity: isHidden ? 0.55 : 1,
                cursor: 'pointer',
              }}
              aria-pressed={!isHidden}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  display: 'inline-block',
                  background: series.color,
                  boxShadow: `0 0 0 1px ${colorScheme === 'dark' ? '#000' : '#fff'} inset, 0 0 0 1px rgba(0,0,0,0.15)`,
                }}
              />
              <span style={{ textDecoration: isHidden ? 'line-through' : 'none' }}>{series.label}</span>
            </button>
          );
        })}
      </div>

      <div style={{ width: '100%', height: 500 }}>
        <HighchartsReact ref={chartRef} highcharts={Highcharts} options={chartOptions} constructorType="chart" />
      </div>
    </div>
  );
};

export default CashFlowChart;